import { useEffect, useRef } from "react";
import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import { useMap } from "react-leaflet";

export interface MapViewport {
  center: LatLngExpression;
  zoom?: number;
}

export type MapViewportTransition = "fly" | "set";

export interface MapViewportControllerProps {
  viewport: MapViewport;
  revision?: number;
  transition?: MapViewportTransition;
  minimumZoom?: number;
  invalidateSizeDelay?: number;
}

const getCenterKey = (center: LatLngExpression) => {
  const { lat, lng } = L.latLng(center);

  return `${lat}:${lng}`;
};

/**
 * Shared Leaflet viewport lifecycle: delayed size invalidation on mount,
 * `fly` to a new center when coordinates change, and `set` back to the target
 * viewport when `revision` changes (e.g. applied dashboard filters). With the
 * `set` transition and no revision, only size invalidation runs.
 */
export const MapViewportController = ({
  viewport,
  revision,
  transition = "fly",
  minimumZoom = 0,
  invalidateSizeDelay = 100,
}: MapViewportControllerProps) => {
  const map = useMap();
  const previousCenterKey = useRef<string>();
  const previousRevision = useRef<number>();

  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), invalidateSizeDelay);

    return () => window.clearTimeout(timer);
  }, [invalidateSizeDelay, map]);

  useEffect(() => {
    if (transition === "set") {
      if (revision === undefined || previousRevision.current === revision) return;

      previousRevision.current = revision;
      map.setView(viewport.center, viewport.zoom ?? map.getZoom(), { animate: true });
      return;
    }

    const centerKey = getCenterKey(viewport.center);

    if (previousCenterKey.current === centerKey) return;

    previousCenterKey.current = centerKey;
    map.flyTo(viewport.center, viewport.zoom ?? Math.max(map.getZoom(), minimumZoom), {
      duration: 0.5,
    });
  }, [map, minimumZoom, revision, transition, viewport.center, viewport.zoom]);

  return null;
};

export default MapViewportController;
